import { Radio, type RadioOptionProps } from "@headlessui/react";
import { cva, type VariantProps } from "class-variance-authority";
import { type ReactNode, type Ref } from "react";
import { cn } from "../lib/utils";
import { trackingAttrs } from "../utils";
import { RADIO_CHECKED_CLASSES, RADIO_DISABLED_CLASSES } from "./radioStateClasses";
import { SelectionDot, type SelectionDotSize } from "./SelectionDot";

const chipVariants = cva(
	"relative w-fit flex items-center rounded-md border border-bc-default bg-surface-base text-primary cursor-pointer select-none hover:border-bc-stronger hover:bg-surface-light focus-visible:ring focus-visible:ring-offset-0 focus-visible:ring-brand-accent focus-visible:ring-opacity-30",
	{
		variants: {
			// Heights include the 1px border on each side: sm 24px, compact 34px, md 40px.
			// `md` and `compact` pair with the two form-input frames (`form/inputs/inputSize`)
			// and share its type scale, so a chip sits flush with a text field of the same size.
			size: {
				sm: "px-2 py-0.5 gap-1 text-sm font-medium leading-[20px]",
				compact: "px-[12px] py-[6px] gap-2 text-[14px] leading-[20px]",
				md: "px-[12px] py-[9px] gap-2 text-[14px] leading-[20px]",
			},
		},
		defaultVariants: { size: "md" },
	},
);

type ChipVariantProps = VariantProps<typeof chipVariants>;

export type RadioChipSize = NonNullable<ChipVariantProps["size"]>;

const DOT_SIZE = { sm: "sm", compact: "sm", md: "md" } as const satisfies Record<
	RadioChipSize,
	SelectionDotSize
>;

export type RadioChipProps<TValue> = Omit<
	RadioOptionProps<"div", TValue>,
	"className" | "children"
> &
	ChipVariantProps & {
		children?: ReactNode;
		className?: string;
		/** Analytics identifier — expands to `data-pendo-id` + `data-amp-track-id`. */
		trackingId?: string;
		ref?: Ref<HTMLDivElement>;
	};

export const RadioChip = <TValue,>({
	size,
	trackingId,
	className,
	children,
	ref,
	...rest
}: RadioChipProps<TValue>) => (
	<Radio
		ref={ref}
		{...rest}
		className={({ checked, disabled }) =>
			cn(
				chipVariants({ size }),
				checked && RADIO_CHECKED_CLASSES,
				disabled && RADIO_DISABLED_CLASSES,
				className,
			)
		}
		{...trackingAttrs(trackingId)}
	>
		{({ checked, disabled }) => (
			<>
				<SelectionDot
					checked={checked}
					size={DOT_SIZE[size ?? "md"]}
					state={disabled ? "disabled" : "default"}
				/>
				<span>{children}</span>
			</>
		)}
	</Radio>
);
